import { programAllows } from "utils";

import { BNP_CARBON_PROGRAM_ID } from "consts";

import { CarbonProgram } from "models";

export type HomeSegment = "no-program" | "removals" | "removals-footprinting" | "fields-footprinting" | "digital-agronomist" | "footprinting-only";

export type HomeStatusCardKind = "payout" | "fieldManagement" | "footprinting";

/** the segments whose payout card has a field management part (outcomes run, allotment) */
export const hasFieldManagementPayout = (segment: HomeSegment): boolean => segment === "removals" || segment === "removals-footprinting";

/**
 * The home page segment of a farm, from its program's platform features.
 * Field management without a wallet plus footprinting counts as footprinting-only, unless the program shows the SoilSight
 * insights panel: then the fields matter to the farmer as well. Combinations that match no segment fall back to "no-program".
 */
export const getHomeSegment = (carbonProgram: CarbonProgram | null | undefined): HomeSegment => {
  if (!carbonProgram) return "no-program";

  const fields = programAllows(carbonProgram, "fields");
  const payout = programAllows(carbonProgram, "payout");
  // BNP stays field management only, even when footprinting is switched on for one of its farms (the API merges that into the program)
  const footprinting = programAllows(carbonProgram, "footprinting") && carbonProgram.id !== BNP_CARBON_PROGRAM_ID;

  if (fields && payout) return footprinting ? "removals-footprinting" : "removals";
  if (fields && footprinting) return carbonProgram.platformFeatures?.fieldManagement?.insightsImpactPanel ? "fields-footprinting" : "footprinting-only";
  if (fields) return "digital-agronomist";
  if (footprinting) return "footprinting-only";
  return "no-program";
};

const CARDS_BY_SEGMENT: Record<HomeSegment, HomeStatusCardKind[]> = {
  "no-program": [],
  removals: ["payout", "fieldManagement"],
  "removals-footprinting": ["payout", "fieldManagement", "footprinting"],
  "fields-footprinting": ["payout", "fieldManagement", "footprinting"],
  "digital-agronomist": [],
  "footprinting-only": ["payout", "footprinting"],
};

/** footprinting pays this year: its payouts are switched on and at least one form has a bonus for the year */
export const paysFootprintingBonus = (carbonProgram: CarbonProgram | null | undefined, year: number): boolean => {
  if (!carbonProgram?.platformFeatures?.footprinting?.payouts) return false;
  const bonuses = carbonProgram.farmLevelFootprinting?.formSubmissionBonuses;
  return [bonuses?.farm, bonuses?.crop, bonuses?.dairy, bonuses?.beef].some((bonusByYear) => (bonusByYear?.[year] ?? 0) > 0);
};

// the program pays for fields or for footprinting; otherwise the payout card has nothing to show
const canEarnPayout = (carbonProgram: CarbonProgram | null | undefined, footprintingYear: number) =>
  programAllows(carbonProgram, "payout") || paysFootprintingBonus(carbonProgram, footprintingYear);

export const cardsForSegment = (segment: HomeSegment, carbonProgram: CarbonProgram | null | undefined, footprintingYear: number): HomeStatusCardKind[] =>
  CARDS_BY_SEGMENT[segment].filter((card) => card !== "payout" || canEarnPayout(carbonProgram, footprintingYear));

/**
 * What the hero shows for the farm's segment. A Soil+ 3 farm without fields has no allotment and nothing for its payout and
 * field management cards yet: it keeps only its footprinting card when the program has footprinting, and otherwise shows the
 * no-program version.
 */
export const getHeroView = (
  segment: HomeSegment,
  carbonProgram: CarbonProgram | null | undefined,
  footprintingYear: number,
  { usesAllotment, hasFields }: { usesAllotment: boolean; hasFields: boolean }
): { segment: HomeSegment; cards: HomeStatusCardKind[] } => {
  const cards = cardsForSegment(segment, carbonProgram, footprintingYear);
  if (!usesAllotment || hasFields || (segment !== "removals" && segment !== "removals-footprinting")) return { segment, cards };
  const footprintingCards = cards.filter((card) => card === "footprinting");
  return footprintingCards.length ? { segment, cards: footprintingCards } : { segment: "no-program", cards: [] };
};
